import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Grid,
  CssBaseline,
  useTheme,
  styled,
  Divider
} from '@mui/material';
import AuthSection from './AuthSection';
import VerifySection from './VerifySection';
import ProductFetchSection from './ProductFetchSection';
import ProductInsertSection from './ProductInsertSection';
import ProductDeleteSection from './ProductDeleteSection';
import SnackbarMessage from './SnackbarMessage';
import { ShoppingCart, Verified, CloudUpload, CloudDownload, Delete } from '@mui/icons-material';

const SectionIcon = ({ id }) => {
  const icons = {
    1: <ShoppingCart fontSize="large" />,
    2: <Verified fontSize="large" />,
    3: <CloudDownload fontSize="large" />,
    4: <CloudUpload fontSize="large" />,
    5: <Delete fontSize="large" />
  };
  return icons[id] || null;
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8]
  },
  borderLeft: `4px solid ${theme.palette.primary.main}`
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`
}));

const GoogleMerchantPage = () => {
  const theme = useTheme();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const sections = [
    { id: 1, title: 'Get Auth URL', Component: AuthSection },
    { id: 2, title: 'Verify Auth Code', Component: VerifySection },
    { id: 3, title: 'Fetch Products', Component: ProductFetchSection },
    { id: 4, title: 'Insert Product', Component: ProductInsertSection },
    { id: 5, title: 'Delete Product', Component: ProductDeleteSection },
  ];

  return (
    <>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.grey[100]} 100%)`,
          py: 8,
          px: { xs: 2, sm: 4, md: 6 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg" sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            fontWeight="800"
            gutterBottom
            sx={{ 
              color: theme.palette.primary.dark,
              mb: 2,
              [theme.breakpoints.down('sm')]: {
                fontSize: '2rem'
              }
            }}
          >
            Google Merchant Integration
          </Typography>

          <Divider sx={{ 
            width: 100, 
            height: 4, 
            backgroundColor: theme.palette.secondary.main,
            margin: '0 auto 20px auto'
          }} />

          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ 
              maxWidth: 700,
              margin: '0 auto',
              fontSize: '1.1rem',
              lineHeight: 1.6
            }}
          >
            Seamlessly manage your Google Merchant Center authorization and product synchronization
            through our intuitive dashboard interface.
          </Typography>
        </Container>

        <Container maxWidth="xl" sx={{ flexGrow: 1 }}>
          <Grid container spacing={4}>
            {sections.map(({ id, title, Component }) => (
              <Grid
                key={id}
                item
                xs={12}
                sm={6}
                lg={4}
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                <StyledPaper elevation={4}>
                  <SectionHeader>
                    <Box sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.light,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: theme.palette.primary.contrastText
                    }}>
                      <SectionIcon id={id} />
                    </Box>
                    <Typography
                      variant="h5"
                      fontWeight="600"
                    >
                      {title}
                    </Typography>
                  </SectionHeader>

                  <Box sx={{ flexGrow: 1 }}>
                    <Component showSnackbar={showSnackbar} />
                  </Box>
                </StyledPaper>
              </Grid>
            ))}
          </Grid>
        </Container>

        <SnackbarMessage snackbar={snackbar} setSnackbar={setSnackbar} />
      </Box>
    </>
  );
};

export default GoogleMerchantPage;